import { useCallback, useEffect, useRef } from 'react';
import { NoteDto, NoteItemType } from '@act/shared/data-transfer-objects';
import { notesRequests } from 'redux/requests/notes.request';
import { useApiCall } from 'redux/utils/apiHook.hook';

export interface UseNoteForItemOptions {
  itemType: NoteItemType;
  itemId: string;
  /** Member the note belongs to — required to create the note row on first save. */
  userId?: string;
  /** Skip the lookup (e.g. parent not created yet). */
  enabled?: boolean;
  /**
   * Known note id from the parent payload (e.g. `visit.noteId`). When provided,
   * the by-item lookup is skipped entirely — we already know what to PATCH.
   */
  noteId?: number;
}

export interface UseNoteForItemResult {
  note: NoteDto | null;
  isLoading: boolean;
  isSaving: boolean;
  saveError: unknown;
  /** Create-or-update the unified note for this item (fire-and-forget). */
  saveText: (text: string) => void;
}

/**
 * Reads (and lazily creates) the single unified `notes.note` linked to a parent
 * item — the edit-flow counterpart to {@link useNoteDraft}. The first save
 * creates the note row; subsequent saves patch it. Built on `useApiCall`.
 */
export function useNoteForItem({
  itemType,
  itemId,
  userId,
  enabled = true,
  noteId,
}: UseNoteForItemOptions): UseNoteForItemResult {
  const [note, isLoading, getNoteByItem] = useApiCall(
    notesRequests.getNoteByItem
  );
  const [updated, isUpdating, updateNote, updateError] = useApiCall(
    notesRequests.updateNote
  );
  const [created, isCreating, createItemNote, createError] = useApiCall(
    notesRequests.createItemNote
  );

  const noteIdRef = useRef<number | null>(noteId ?? null);

  // Keep the ref in sync if the parent supplies / changes the known id.
  useEffect(() => {
    if (noteId != null) noteIdRef.current = noteId;
  }, [noteId]);

  useEffect(() => {
    // Only look up the note when we don't already know its id.
    if (!enabled || !itemType || !itemId || noteId != null) return;
    getNoteByItem({ params: { itemType, itemId } });
    // `getNoteByItem` is a fresh closure each render; depend only on the query.
  }, [enabled, itemType, itemId, noteId]);

  // Capture the note id from the initial fetch and from the first create.
  useEffect(() => {
    if (note?.id) noteIdRef.current = note.id;
  }, [note?.id]);
  useEffect(() => {
    if (created?.id) noteIdRef.current = created.id;
  }, [created?.id]);

  const saveText = useCallback(
    (text: string) => {
      if (noteIdRef.current) {
        updateNote({
          params: { noteId: noteIdRef.current },
          body: { noteId: noteIdRef.current, text },
        });
        return;
      }
      createItemNote({
        body: {
          itemType,
          itemId,
          ...(userId ? { userId } : {}),
          text,
        },
      });
    },
    [itemType, itemId, userId, updateNote, createItemNote]
  );

  return {
    note: (updated ?? created ?? note) ?? null,
    isLoading: !!isLoading,
    isSaving: !!isUpdating || !!isCreating,
    saveError: updateError ?? createError,
    saveText,
  };
}

export default useNoteForItem;
